import { LanguagesEnum } from '@/api/enums';
import {
  LoadingStatuses,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import {
  CourseChangesReview,
  useCourseChangesPendingEdits,
  useSaveCourseChanges,
} from '@/components/courses/course-changes';
import { useCourseChanges } from '@/hooks/query/course-changes.hooks';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { AppButton } from '@/ui';
import { FlexContainer } from '@/ui/styled-ui';
import { GrayMagicPencilIcon } from '@/utils/helpers';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ButtonContainer,
  ContentCard,
  EmptyState,
  ErrorState,
  IntroText,
} from './course-changes/CourseChangesPage.styles';
import { CoursesActionHeader } from './CoursesActionHeader';
import { CoursesTabs } from './CoursesTabs';

export const CourseChangesPage: React.FC = () => {
  const { t } = useTranslation();
  const { id: courseId } = useParams<{ id: string }>();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const {
    data: changes,
    isLoading,
    error,
    refetch,
  } = useCourseChanges(currentAccount?.id, courseId);
  const [isSaving, setIsSaving] = useState(false);
  const {
    pendingSlideEdits,
    pendingQuestionEdits,
    handleSlideEdit,
    handleQuestionEdit,
    clearPendingEdits,
  } = useCourseChangesPendingEdits();

  // Get the language from the customized course (the language it was customized for). Should never be undefined.
  const customizationLanguage = changes?.customizedCourse
    ?.defaultLanguage as LanguagesEnum;

  const { saveChanges } = useSaveCourseChanges(
    courseId,
    changes,
    refetch,
    customizationLanguage,
  );
  const navigate = useNavigate();

  const handleSaveChanges = async () => {
    setIsSaving(true);

    try {
      await saveChanges(pendingSlideEdits, pendingQuestionEdits);

      clearPendingEdits();
    } finally {
      setIsSaving(false);
    }
  };

  // Render header and tabs if course is available
  const renderHeaderAndTabs = () => {
    if (!changes?.customizedCourse) return null;
    return (
      <>
        <CoursesActionHeader course={changes.customizedCourse} />
        <CoursesTabs course={changes.customizedCourse} />
      </>
    );
  };

  const handleContinueToPreview = () => {
    navigate(
      RedirectPaths[RedirectPathsEnum.COURSES_SCORM_PREVIEW](courseId || ''),
    );
  };

  const hasPendingEdits =
    pendingSlideEdits.length > 0 || pendingQuestionEdits.length > 0;

  // Loading state
  if (isLoading) {
    return (
      <FlexContainer direction="column" className="mt-5">
        <ProgressSpinner />
        <h3>{t('course.changes.loading')}</h3>
      </FlexContainer>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        {renderHeaderAndTabs()}
        <ErrorState>
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: '48px', marginBottom: '16px' }}
          />
          <h3>{t('course.changes.error.title')}</h3>
          <p>{t('course.changes.error.message')}</p>
        </ErrorState>
      </>
    );
  }

  // No changes found
  if (!changes || !changes.customizedCourse) {
    return (
      <>
        {renderHeaderAndTabs()}
        <EmptyState>
          <i
            className="pi pi-info-circle"
            style={{ fontSize: '48px', marginBottom: '16px' }}
          />
          <h3>{t('course.changes.notFound.title')}</h3>
          <p>{t('course.changes.notFound.message')}</p>
        </EmptyState>
      </>
    );
  }

  return (
    <>
      {renderHeaderAndTabs()}
      <ContentCard>
        {/* Intro Section */}
        <FlexContainer align="center" justify="start" gap={19}>
          <GrayMagicPencilIcon />
          <IntroText>
            <span className="bold">{t('course.changes.intro.title')}</span>
            <br />
            {t('course.changes.intro.description')}
          </IntroText>
        </FlexContainer>

        {/* Content Changes */}
        <CourseChangesReview
          courseChanges={changes}
          onSlideEdit={handleSlideEdit}
          onQuestionEdit={handleQuestionEdit}
          pendingSlideEdits={pendingSlideEdits}
          pendingQuestionEdits={pendingQuestionEdits}
        />

        <ButtonContainer>
          {hasPendingEdits && (
            <AppButton
              label={t('button.save')}
              onClick={handleSaveChanges}
              isDisabled={isSaving}
              state={isSaving ? LoadingStatuses.LOADING : LoadingStatuses.IDLE}
            />
          )}
          <AppButton onClick={handleContinueToPreview}>
            {t('course.customization.review.continueToPreview')}
          </AppButton>
        </ButtonContainer>
      </ContentCard>
    </>
  );
};
